import React from "react";
import {
  YhTableColumn,
  YhTableCellParams,
} from "../../ui/YhTable";
import { OrgNameCell } from "../Organization/OrganizationSelect";

const cellDash = (value: unknown) => {
  if (value == null) return "—";
  if (typeof value === "string" && !value.trim()) return "—";
  return String(value);
};

const hasAny = (rows: any[], predicate: (row: any) => boolean) =>
  Array.isArray(rows) && rows.some(predicate);

const INVITED_BY_RE = /invited\s*by|referred\s*by|referrer|invited\s*from/i;

export function invitedByOf(row: any): string {
  const direct = String(row?.invitedByName || "").trim();
  if (direct) return direct;
  const entries = Array.isArray(row?.labeledAnswers) ? row.labeledAnswers : [];
  const hit = entries.find((entry: any) =>
    INVITED_BY_RE.test(String(entry?.label || ""))
  );
  return String(hit?.value || "").trim();
}

export type BuildFormSubmissionColumnsOpts = {
  rows: any[];
  orgNameMap: Record<string, string>;
  onViewAnswers: (row: any) => void;
  onOpenLead: (leadId: string) => void;
  /**
   * Optional: convert a guest submission (no leadId) into a CRM lead.
   * When omitted, rows without a lead show "—" (not a Guest badge).
   */
  onConvertToLead?: (row: any) => void;
  convertingId?: string | null;
  /** Show rating column only when at least one row has a rating (default true). */
  preferRating?: boolean;
  /** Feedback list: never label rows as Guest / Guest link. */
  hideGuestLabels?: boolean;
  /**
   * Replace the static Customer column with columns built from each
   * submission's labeled form-field answers (dynamic per form).
   */
  dynamicFormFields?: boolean;
  /** Max dynamic answer columns to show (default 6). */
  maxDynamicFields?: number;
};

const SKIP_DYNAMIC_LABEL_RE =
  /^(team\s*member|invited\s*by|referred\s*by|rating|overall\s*rating)$/i;

function answerValueForLabel(row: any, label: string): string {
  const entries = Array.isArray(row?.labeledAnswers) ? row.labeledAnswers : [];
  const hit = entries.find(
    (entry: any) =>
      String(entry?.label || "").trim().toLowerCase() ===
      String(label || "").trim().toLowerCase()
  );
  if (hit) return formatAnswerValue(hit.value);
  // Fallback: answers keyed by id when labeledAnswers missing that label
  return "—";
}

function collectDynamicFieldLabels(
  rows: any[],
  maxFields: number
): { id: string; label: string }[] {
  const order: string[] = [];
  const counts = new Map<string, number>();
  const idByLabel = new Map<string, string>();

  for (const row of rows || []) {
    const entries = Array.isArray(row?.labeledAnswers)
      ? row.labeledAnswers
      : [];
    for (const entry of entries) {
      const label = String(entry?.label || "").trim();
      if (!label || SKIP_DYNAMIC_LABEL_RE.test(label)) continue;
      const key = label.toLowerCase();
      if (!counts.has(key)) {
        order.push(key);
        idByLabel.set(key, String(entry?.id || label));
      }
      counts.set(key, (counts.get(key) || 0) + 1);
    }
  }

  // Prefer fields that appear most often, keep first-seen order as tie-break.
  const ranked = [...order].sort((a, b) => {
    const diff = (counts.get(b) || 0) - (counts.get(a) || 0);
    if (diff !== 0) return diff;
    return order.indexOf(a) - order.indexOf(b);
  });

  return ranked.slice(0, Math.max(1, maxFields)).map((key) => {
    // Restore original casing from first matching row
    for (const row of rows || []) {
      const entries = Array.isArray(row?.labeledAnswers)
        ? row.labeledAnswers
        : [];
      const hit = entries.find(
        (e: any) => String(e?.label || "").trim().toLowerCase() === key
      );
      if (hit?.label) {
        return {
          id: String(hit.id || idByLabel.get(key) || hit.label),
          label: String(hit.label),
        };
      }
    }
    return { id: idByLabel.get(key) || key, label: key };
  });
}

function customerDisplayOf(row: any) {
  const name = String(row?.leadName || "").trim();
  if (name) return name;
  const entries = Array.isArray(row?.labeledAnswers) ? row.labeledAnswers : [];
  for (const entry of entries) {
    const label = String(entry?.label || "");
    const value = String(entry?.value || "").trim();
    if (
      value &&
      /name/i.test(label) &&
      !/user\s*name|file\s*name|form\s*name|team|invited|project/i.test(label)
    ) {
      return value;
    }
  }
  return "";
}

function customerContactOf(row: any) {
  const phone = String(row?.leadPhone || "").trim();
  const email = String(row?.leadEmail || "").trim();
  if (phone || email) {
    return [phone, email].filter(Boolean).join(" · ");
  }
  const entries = Array.isArray(row?.labeledAnswers) ? row.labeledAnswers : [];
  let fromPhone = "";
  let fromEmail = "";
  for (const entry of entries) {
    const label = String(entry?.label || "");
    const value = String(entry?.value || "").trim();
    if (!value) continue;
    if (!fromPhone && /phone|mobile|whatsapp/i.test(label)) fromPhone = value;
    if (!fromEmail && /e-?mail/i.test(label)) fromEmail = value;
  }
  return [fromPhone, fromEmail].filter(Boolean).join(" · ");
}

export function buildFormSubmissionColumns({
  rows,
  orgNameMap,
  onViewAnswers,
  onOpenLead,
  onConvertToLead,
  convertingId = null,
  preferRating = true,
  hideGuestLabels = false,
  dynamicFormFields = false,
  maxDynamicFields = 6,
}: BuildFormSubmissionColumnsOpts): YhTableColumn[] {
  const showRating =
    preferRating && hasAny(rows, (r) => r?.rating != null && r.rating !== "");
  const showLeadSource = hasAny(
    rows,
    (r) => Boolean(String(r?.leadSource || "").trim())
  );
  const showTeamMember = hasAny(
    rows,
    (r) => Boolean(String(r?.teamMemberName || "").trim())
  );
  const showInvitedBy = hasAny(
    rows,
    (r) => Boolean(String(invitedByOf(r) || "").trim())
  );
  const showBulkSet = hasAny(
    rows,
    (r) =>
      Boolean(r?.bulkJobId) || Boolean(String(r?.bulkJobName || "").trim())
  );
  const showProject = hasAny(
    rows,
    (r) =>
      Boolean(String(r?.project_name || "").trim()) ||
      Boolean(String(r?.projectSlug || "").trim()) ||
      r?.formScope === "organization"
  );

  const dynamicFields = dynamicFormFields
    ? collectDynamicFieldLabels(rows, maxDynamicFields)
    : [];

  const columns: YhTableColumn[] = [];

  if (dynamicFormFields) {
    if (dynamicFields.length) {
      for (const field of dynamicFields) {
        columns.push({
          headerName: field.label,
          field: `answer:${field.id}`,
          minWidth: 140,
          flex: 1,
          renderCell: (params: YhTableCellParams) => (
            <span
              className="line-clamp-2 text-sm text-[#111827]"
              title={answerValueForLabel(params.row, field.label)}
            >
              {answerValueForLabel(params.row, field.label)}
            </span>
          ),
        });
      }
    } else {
      columns.push({
        headerName: "Responses",
        field: "labeledAnswers",
        minWidth: 220,
        flex: 1,
        renderCell: (params: YhTableCellParams) => {
          const entries = submissionAnswerEntries(params.row).slice(0, 3);
          if (!entries.length) {
            return <span className="text-sm text-[#9CA3AF]">—</span>;
          }
          return (
            <div className="flex min-w-0 flex-col items-start gap-0.5">
              {entries.map((entry) => (
                <span
                  key={entry.id}
                  className="truncate text-xs text-[#374151]"
                  title={`${entry.label}: ${entry.value}`}
                >
                  <span className="font-semibold text-[#6B7280]">
                    {entry.label}:
                  </span>{" "}
                  {entry.value}
                </span>
              ))}
            </div>
          );
        },
      });
    }
  } else {
    columns.push({
      headerName: "Customer",
      field: "leadName",
      minWidth: 200,
      flex: 1,
      renderCell: (params: YhTableCellParams) => {
        const name = customerDisplayOf(params.row);
        const contact = customerContactOf(params.row);
        return (
          <div className="flex min-w-0 flex-col items-start gap-0.5">
            <span className="truncate text-sm font-semibold capitalize text-[#111827]">
              {name || "—"}
            </span>
            <span className="truncate text-xs text-[#6B7280]">
              {contact || "—"}
            </span>
          </div>
        );
      },
    });
  }

  columns.push({
    headerName: "Lead",
    field: "leadId",
    minWidth: onConvertToLead ? 160 : 120,
    renderCell: (params: YhTableCellParams) => {
      const leadId = params.row?.leadId ? String(params.row.leadId) : "";
      const rowId = params.row?._id ? String(params.row._id) : "";
      const busy = convertingId && convertingId === rowId;
      if (!leadId) {
        if (onConvertToLead) {
          return (
            <button
              type="button"
              disabled={!!busy}
              className="text-left text-xs font-semibold text-[#A02448] hover:underline disabled:opacity-60"
              title="Create a CRM lead from this registration"
              onClick={(e) => {
                e.stopPropagation();
                onConvertToLead(params.row);
              }}
            >
              {busy ? "Converting…" : "Convert to lead"}
            </button>
          );
        }
        if (hideGuestLabels || dynamicFormFields) {
          return (
            <span className="text-xs font-medium text-[#9CA3AF]">—</span>
          );
        }
        return (
          <span className="inline-flex rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-800">
            Guest link
          </span>
        );
      }
      return (
        <button
          type="button"
          className="truncate text-left text-xs font-semibold text-[#1E3A5F] hover:underline"
          title="Open lead details"
          onClick={(e) => {
            e.stopPropagation();
            onOpenLead(leadId);
          }}
        >
          View lead
        </button>
      );
    },
  });

  if (showTeamMember) {
    columns.push({
      headerName: "Team member",
      field: "teamMemberName",
      minWidth: 160,
      renderCell: (params: YhTableCellParams) => (
        <span className="truncate text-sm font-medium capitalize text-[#111827]">
          {cellDash(params.row?.teamMemberName)}
        </span>
      ),
    });
  }

  if (showInvitedBy) {
    columns.push({
      headerName: "Invited by",
      field: "invitedByName",
      minWidth: 140,
      renderCell: (params: YhTableCellParams) => (
        <span className="truncate text-sm font-medium capitalize text-[#111827]">
          {cellDash(invitedByOf(params.row))}
        </span>
      ),
    });
  }

  if (showLeadSource) {
    columns.push({
      headerName: "Lead source",
      field: "leadSource",
      minWidth: 120,
      renderCell: (params: YhTableCellParams) => (
        <span className="truncate text-sm capitalize text-[#374151]">
          {cellDash(params.row?.leadSource)}
        </span>
      ),
    });
  }

  if (showBulkSet) {
    columns.push({
      headerName: "Bulk upload",
      field: "bulkJobName",
      minWidth: 160,
      renderCell: (params: YhTableCellParams) => (
        <div className="flex min-w-0 flex-col items-start gap-0.5">
          <span className="truncate text-sm font-medium text-[#111827]">
            {cellDash(params.row?.bulkJobName)}
          </span>
          {params.row?.bulkJobId ? (
            <span className="truncate font-mono text-[10px] text-[#9CA3AF]">
              {String(params.row.bulkJobId).slice(-8)}
            </span>
          ) : null}
        </div>
      ),
    });
  }

  columns.push({
    headerName: "Organization",
    field: "organizationId",
    minWidth: 150,
    renderCell: (params: YhTableCellParams) => (
      <OrgNameCell
        organizationId={params.row?.organizationId}
        map={orgNameMap}
        fallbackLabel={params.row?.organizationName}
      />
    ),
  });

  if (showProject) {
    columns.push({
      headerName: "Project",
      field: "project_name",
      minWidth: 140,
      renderCell: (params: YhTableCellParams) => (
        <span className="truncate text-sm capitalize text-[#374151]">
          {params.row?.project_name ||
            params.row?.projectSlug ||
            (params.row?.formScope === "organization" ? "Org level" : "—")}
        </span>
      ),
    });
  }

  columns.push({
    headerName: "Form",
    field: "form_name",
    minWidth: 160,
    renderCell: (params: YhTableCellParams) => (
      <div className="flex min-w-0 flex-col items-start gap-0.5">
        <span className="truncate text-sm font-semibold text-[#111827]">
          {cellDash(params.row?.form_name)}
        </span>
        <span className="text-[11px] capitalize text-[#6B7280]">
          {params.row?.formScope || "organization"}
        </span>
      </div>
    ),
  });

  if (showRating) {
    columns.push({
      headerName: "Rating",
      field: "rating",
      minWidth: 90,
      renderCell: (params: YhTableCellParams) => (
        <span className="text-sm font-semibold text-[#D97706]">
          {params.row?.rating != null ? `${params.row.rating}★` : "—"}
        </span>
      ),
    });
  }

  columns.push(
    {
      headerName: "Form source",
      field: "source",
      minWidth: 140,
      renderCell: (params: YhTableCellParams) => {
        const label =
          params.row?.formSource || params.row?.source || "public";
        const channel = String(params.row?.source || "").toLowerCase();
        return (
          <div className="flex min-w-0 flex-col items-start gap-0.5">
            <span className="inline-flex rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold capitalize text-slate-700">
              {cellDash(label)}
            </span>
            {channel &&
            channel !== String(label).toLowerCase() &&
            ["meta", "google", "link", "whatsapp", "token"].includes(
              channel
            ) ? (
              <span className="text-[10px] capitalize text-[#9CA3AF]">
                {channel === "meta"
                  ? "Meta ads"
                  : channel === "link"
                    ? "Manual link"
                    : channel}
              </span>
            ) : null}
          </div>
        );
      },
    },
    {
      headerName: "Submitted",
      field: "createdAt",
      minWidth: 150,
      renderCell: (params: YhTableCellParams) => {
        const d = params.row?.createdAt
          ? new Date(params.row.createdAt)
          : null;
        return (
          <span className="text-xs text-[#6B7280]">
            {d && !Number.isNaN(d.getTime()) ? d.toLocaleString() : "—"}
          </span>
        );
      },
    },
    {
      headerName: "Answers",
      field: "answers",
      minWidth: 90,
      renderCell: (params: YhTableCellParams) => (
        <button
          type="button"
          className="text-xs font-semibold text-[#1E3A5F] hover:underline"
          onClick={(e) => {
            e.stopPropagation();
            onViewAnswers(params.row);
          }}
        >
          View
        </button>
      ),
    }
  );

  return columns;
}

export function formatAnswerValue(value: unknown): string {
  if (value == null || value === "") return "—";
  if (Array.isArray(value)) {
    return value.length ? value.map((v) => String(v)).join(", ") : "—";
  }
  if (typeof value === "object") {
    const rec = value as Record<string, unknown>;
    const named = String(rec.name || rec.label || rec.value || "").trim();
    if (named) return named;
    try {
      return JSON.stringify(value);
    } catch {
      return "—";
    }
  }
  return String(value);
}

export function submissionAnswerEntries(detail: any): {
  id: string;
  label: string;
  value: string;
}[] {
  if (Array.isArray(detail?.labeledAnswers) && detail.labeledAnswers.length) {
    return detail.labeledAnswers.map((row: any) => ({
      id: String(row?.id || row?.label || ""),
      label: String(row?.label || row?.id || "Field"),
      value: formatAnswerValue(row?.value),
    }));
  }
  return Object.entries(detail?.answers || {}).map(([id, value]) => ({
    id,
    label: id,
    value: formatAnswerValue(value),
  }));
}
